// src/services/queries/season.ts

import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Season, QueryParams } from '@/model';
import { SeasonConverter } from '@/converters/season';
import { PostSeasonRequest, PutSeasonCurrentStageRequest, PutSeasonRequest } from '../request/season';
import { SeasonService } from '../instances/season';

/**
 * Lista de temporadas (GET /competitions/seasons)
 */
export const useSeasons = (params?: QueryParams) => {
  const queryClient = useQueryClient();

  const queryKey = useMemo(() => ['GET_SEASONS', params], [params]);

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const [err, data] = await SeasonService.getSeasons(params);
      if (err) {
        throw err;
      }
      return {
        data: SeasonConverter.toDomainFromPaginatedResponse(data),
        meta: data.meta,
      } as { data: Season[]; meta: typeof data.meta };
    },
    refetchOnWindowFocus: false,
  });

  const forceRefetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  return useMemo(
    () => ({
      ...query,
      forceRefetch,
    }),
    [query, forceRefetch],
  );
};

/**
 * Detalhe de uma temporada (GET /competitions/seasons/:uuid)
 */
export const useSeasonById = (id: string) => {
  const queryKey = useMemo(() => ['GET_SEASON_BY_ID', id], [id]);

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const [err, data] = await SeasonService.getSeasonById(id);
      if (err) {
        throw err;
      }
      return SeasonConverter.toDomainFromByIdResponse(data);
    },
    enabled: !!id,
    refetchOnWindowFocus: false,
  });

  const forceRefetch = useCallback(() => {
    query.refetch();
  }, [query]);

  return useMemo(
    () => ({
      ...query,
      forceRefetch,
    }),
    [query, forceRefetch],
  );
};

/**
 * Criar temporada (POST /competitions/seasons)
 */
export const usePostSeasonMutation = () => {
  return useMutation({
    mutationKey: ['POST_SEASON'],
    mutationFn: async (payload: PostSeasonRequest) => {
      const [err, data] = await SeasonService.postSeason(payload);
      if (err) {
        throw err;
      }
      return SeasonConverter.toDomainFromPostResponse(data);
    },
  });
};

/**
 * Edita temporada (PUT /competitions/seasons/:id)
 */
export const usePutSeasonMutation = () => {
  return useMutation({
    mutationKey: ['PUT_SEASON'],
    mutationFn: async ({ id, payload }: { id: string; payload: PutSeasonRequest }) => {
      const [err, data] = await SeasonService.putSeason(id, payload);
      if (err) {
        throw err;
      }
      return SeasonConverter.toDomainFromPutResponse(data);
    },
  });
};

/**
 * Deletar temporada (DELETE /competitions/seasons/:uuid)
 */
export const useDeleteSeasonMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_SEASON'],
    mutationFn: async (id: string) => {
      const [err] = await SeasonService.deleteSeason(id);
      if (err) {
        throw err;
      }
      return;
    },
  });
};

/**
 * Atualizar fase atual da temporada
 * (PUT /competitions/seasons/:uuid/current-stage)
 */
export const usePutSeasonCurrentStageMutation = () => {
  return useMutation({
    mutationKey: ['PUT_SEASON_CURRENT_STAGE'],
    mutationFn: async ({ id, payload }: { id: string; payload: PutSeasonCurrentStageRequest }) => {
      const [err, data] = await SeasonService.putSeasonCurrentStage(id, payload);
      if (err) {
        throw err;
      }
      return SeasonConverter.toDomainFromPutCurrentStageResponse(data);
    },
  });
};

/**
 * Se você quiser um "GET por ID" via mutation (tipo buscar sob demanda),
 * equivalente ao useGetChampionshipByIdMutation:
 */
export const useGetSeasonByIdMutation = () => {
  return useMutation({
    mutationKey: ['GET_SEASON_BY_ID_MUTATION'],
    mutationFn: async (uuid: string) => {
      const [err, data] = await SeasonService.getSeasonById(uuid);
      if (err) {
        throw err;
      }
      return SeasonConverter.toDomainFromByIdResponse(data);
    },
  });
};
